import type { TextLineHeightModifierValue } from "schemas/generated/fileModifiers";

import React from "react";

import LineSpacing from "@svg/modifiers/line-spacing";

import { LengthInputSelector } from "@v2/features/modifiers/components/LengthInputSelector";
import { LINE_HEIGHT_OPTIONS } from "@v2/features/modifiers/constants";
import { useOptimisticModifierValue } from "@v2/features/modifiers/hooks/useOptimisticModifierValue";
import { ChevronDown } from "lucide-react";
import { parseUnit } from "replo-runtime/shared/utils/units";

interface LineHeightModifierProps {
  onChange: (modifiers: TextLineHeightModifierValue[]) => void;
}

const LineHeightModifier: React.FC<LineHeightModifierProps> = ({
  onChange,
}) => {
  const currentValue =
    useOptimisticModifierValue({
      modifierType: { type: "lineHeight", value: 1.5 },
      extractor: (elementDesignation) => {
        return elementDesignation.element.styleOverrides?.lineHeight ?? null;
      },
    }) ?? null;

  // NOTE (Kevin, 2025-12-10): Convert unitless multiplier (1.5) to percentage (150%) for display.
  // When currentValue is null, display "Auto" to indicate browser default line height.
  const displayValue = currentValue != null ? `${currentValue * 100}%` : "Auto";

  const menuOptions = LINE_HEIGHT_OPTIONS.map((option) => ({
    label: option.label,
    value: option.value === null ? null : `${option.value * 100}`,
  }));

  const handleChange = (newValue: string | null) => {
    // Handle "Auto" selection - null or "auto" (case-insensitive) means removal
    if (newValue === null || newValue.toLowerCase() === "auto") {
      onChange([{ type: "lineHeight" as const, value: null }]);
      return;
    }

    const result = parseUnit(
      newValue,
      { value: 100, unit: "%" },
      "lineHeight",
      "%",
    );
    onChange([
      {
        type: "lineHeight" as const,
        value: Number(result.value) / 100,
      },
    ]);
  };

  return (
    <div className="line-height-modifier">
      <LengthInputSelector
        field="lineHeight"
        value={displayValue}
        startEnhancer={<LineSpacing />}
        onChange={handleChange}
        placeholder="100%"
        metrics={["%"]}
        unitDefaults={{ value: "100", unit: "%" }}
        menuOptions={menuOptions}
        minValues={{ "%": 100 }}
        inputClassName="w-full"
        endEnhancer={() => <ChevronDown size={12} />}
        allowedNonUnitValues={["Auto", "auto"]}
        transformValue={(value: string) => {
          return value.toLowerCase() === "auto" ? "Auto" : value;
        }}
      />
    </div>
  );
};

export default LineHeightModifier;
